import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';

/**
 * A menu board row.
 *
 * Reuses the theme's quick-add component so "Preview" and "Choose" open the
 * standard quick-add modal. The base class finds the product URL through a
 * surrounding product card; menu rows carry it in a data attribute instead.
 */
class MenuItemComponent extends QuickAddComponent {
  get productPageUrl() {
    const url = this.dataset.productUrl;

    if (!url) return '';

    try {
      return new URL(url, window.location.origin).toString();
    } catch {
      return '';
    }
  }
}

if (!customElements.get('menu-item-component')) {
  customElements.define('menu-item-component', MenuItemComponent);
}

/**
 * @typedef {object} MenuBoardRefs
 * @property {HTMLElement} [nav] - The sticky category navigation
 * @property {HTMLAnchorElement[]} [navLinks] - Category links inside the navigation
 * @property {HTMLElement[]} [categories] - Category cards
 */

/**
 * The menu board: sticky category navigation with scroll spy, and
 * "Show all" toggles for long categories.
 *
 * @extends {Component<MenuBoardRefs>}
 */
class MenuBoardComponent extends Component {
  /** @type {string | null} */
  #activeId = null;
  /** @type {string | null} */
  #pinnedId = null;
  /** @type {boolean} */
  #ticking = false;
  /** @type {number | null} */
  #scrollLockTimeout = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#scroller = this.#findScrollContainer();
    this.#measureNav();
    this.#updateActive();

    // The theme scrolls the page inside a wrapper on some layouts and the
    // window on others, so listen to both.
    this.#scroller?.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroller?.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);

    if (this.#scrollLockTimeout) clearTimeout(this.#scrollLockTimeout);
  }

  /**
   * Scrolls smoothly to the category a navigation link points at.
   * Uses scrollIntoView so it works whichever element scrolls the page;
   * the sticky header and navigation are accounted for by scroll-margin-top.
   * @param {Event} event
   */
  scrollToCategory(event) {
    const link = event.target instanceof Element ? event.target.closest('a') : null;
    const id = link?.getAttribute('href')?.replace(/^#/, '');
    const target = id ? document.getElementById(id) : null;

    if (!id || !target) return;

    event.preventDefault();

    // Keep the chosen category highlighted while it stays under the navigation,
    // even when a neighbouring card shares the same row on desktop.
    this.#pinnedId = id;
    this.#setActive([id]);
    this.#lockScrollSpy(900);

    target.scrollIntoView({ behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });

    if (history.replaceState) history.replaceState(null, '', `#${id}`);
  }

  /**
   * Reveals or hides the items past the per-category limit.
   * @param {Event} event
   */
  toggleItems(event) {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    const card = button?.closest('.menu-board__card');

    if (!button || !card) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';

    for (const item of card.querySelectorAll('.menu-board__item--extra')) {
      item.toggleAttribute('hidden', expanded);
    }

    button.setAttribute('aria-expanded', String(!expanded));
    button.textContent = expanded ? button.dataset.showAll ?? '' : button.dataset.showLess ?? '';

    // When collapsing a long category, keep its heading in view
    if (expanded && card.getBoundingClientRect().top < this.#navOffset()) {
      card.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }

  #handleScroll = () => {
    if (this.#ticking) return;

    this.#ticking = true;

    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#updateActive();
    });
  };

  #handleResize = () => {
    this.#measureNav();
    this.#updateActive();
  };

  /** Finds the nearest ancestor that actually scrolls, or null when the window does. */
  #findScrollContainer() {
    let element = this.parentElement;

    while (element && element !== document.body) {
      const { overflowY } = getComputedStyle(element);

      if (/(auto|scroll)/.test(overflowY) && element.scrollHeight > element.clientHeight) {
        return element;
      }

      element = element.parentElement;
    }

    return null;
  }

  #measureNav() {
    const { nav } = this.refs;
    const height = nav instanceof HTMLElement ? nav.offsetHeight : 0;

    this.style.setProperty('--mb-nav-height', `${height}px`);
  }

  /** Height of the sticky header plus the sticky category navigation. */
  #navOffset() {
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const { nav } = this.refs;
    const navHeight = nav instanceof HTMLElement ? nav.offsetHeight : 0;

    return header + navHeight;
  }

  /**
   * Marks the categories currently under the navigation as active.
   *
   * Cards can sit side by side on desktop, so the whole row that has reached
   * the threshold is highlighted, dropping any card in it that has already
   * scrolled out of view. A category chosen from the navigation stays the
   * only highlight while its card remains in view.
   */
  #updateActive() {
    if (this.#scrollLockTimeout) return;

    const categories = this.refs.categories ?? [];

    if (!categories.length) return;

    const offset = this.#navOffset();
    const visibleTop = offset + 40;
    // A row becomes current once its top passes roughly a third of the visible area
    const threshold = offset + Math.max(window.innerHeight - offset, 0) * 0.3;
    const entries = categories.map((element) => ({ element, rect: element.getBoundingClientRect() }));

    if (this.#pinnedId) {
      const pinned = entries.find(({ element }) => element.dataset.category === this.#pinnedId);

      if (pinned && pinned.rect.top <= threshold && pinned.rect.bottom > visibleTop) {
        this.#setActive([this.#pinnedId]);
        return;
      }

      this.#pinnedId = null;
    }

    // Find the last row whose top has passed the threshold
    let rowTop = null;
    /** @type {typeof entries} */
    let row = [];

    for (const entry of entries) {
      if (entry.rect.top > threshold) break;

      if (rowTop === null || entry.rect.top > rowTop + 2) {
        rowTop = entry.rect.top;
        row = [entry];
      } else {
        row.push(entry);
      }
    }

    if (!row.length) row = [entries[0]];

    let active = row.filter(({ rect }) => rect.bottom > visibleTop);

    if (!active.length) active = [row[row.length - 1]];

    this.#setActive(active.map(({ element }) => element.dataset.category ?? ''));
  }

  /** @param {string[]} ids */
  #setActive(ids) {
    const key = ids.filter(Boolean).join('|');

    if (!key || key === this.#activeId) return;

    this.#activeId = key;

    let revealed = false;

    for (const link of this.refs.navLinks ?? []) {
      const active = ids.includes(link.dataset.category ?? '');

      link.classList.toggle('is-active', active);

      if (active) {
        link.setAttribute('aria-current', 'true');

        if (!revealed) {
          this.#revealLink(link);
          revealed = true;
        }
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Scrolls the navigation strip horizontally so the active pill is visible.
   * @param {HTMLElement} link
   */
  #revealLink(link) {
    const list = link.closest('ul');

    if (!(list instanceof HTMLElement)) return;

    const left = link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2;

    list.scrollTo({ left: Math.max(left, 0), behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  /**
   * Pauses the scroll spy while a programmatic smooth scroll is in flight so
   * the pills don't flicker through every category on the way.
   * @param {number} duration
   */
  #lockScrollSpy(duration) {
    if (this.#scrollLockTimeout) clearTimeout(this.#scrollLockTimeout);

    this.#scrollLockTimeout = window.setTimeout(() => {
      this.#scrollLockTimeout = null;
      this.#updateActive();
    }, duration);
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('menu-board-component')) {
  customElements.define('menu-board-component', MenuBoardComponent);
}
